import { enumOf, useTableParams, type FieldSpec } from "@/hooks/useTableParams";
import type {
  IPerformanceCycleFilters,
  IPerformanceFilters,
  IPerformanceTeamMemberFilters,
} from "@/types/(company)/performance.interface";
import { ENUM_PERFORMANCE_CYCLE_TYPE } from "@/utils/enums/performance.enum";

/**
 * URL-backed toolbar state for the company performance tables.
 *
 * The filter panels offer an "All" option whose value is the literal `all`.
 * It is deliberately NOT in the allowed sets: enum validation drops it, so
 * picking "All" clears the param instead of sending `?cycleType=all` to an API
 * that 400s on an unknown enum.
 */

const CYCLE_DEFAULTS: IPerformanceCycleFilters = { cycleType: "", year: "" };

export const usePerformanceCycleParams = () =>
  useTableParams<IPerformanceCycleFilters>({
    defaults: CYCLE_DEFAULTS,
    schema: {
      cycleType: enumOf(Object.values(ENUM_PERFORMANCE_CYCLE_TYPE)),
      year: "int",
    } satisfies Partial<Record<keyof IPerformanceCycleFilters, FieldSpec>>,
  });

const TEAMS_DEFAULTS: IPerformanceFilters = {
  updatedFrom: null,
  updatedTo: null,
};

export const usePerformanceTeamsParams = () =>
  useTableParams<IPerformanceFilters>({
    defaults: TEAMS_DEFAULTS,
    schema: {
      updatedFrom: "date",
      updatedTo: "date",
    } satisfies Partial<Record<keyof IPerformanceFilters, FieldSpec>>,
    // No `allowAll` on this toolbar's BranchFilterSelect, so no default value.
    branch: { storageKey: "branch_filter_company-performance" },
  });

const TEAM_MEMBER_DEFAULTS: IPerformanceTeamMemberFilters = { roleId: "" };

export const usePerformanceTeamMemberParams = () =>
  useTableParams<IPerformanceTeamMemberFilters>({
    defaults: TEAM_MEMBER_DEFAULTS,
    schema: {
      roleId: "int",
    } satisfies Partial<Record<keyof IPerformanceTeamMemberFilters, FieldSpec>>,
  });
